/**
 * Utility functions for form validation and payload formatting.
 * @module formUtils
 */
export function formUtils() {
  /**
   * Checks the fields of a payload object for validation errors.
   * @param {Object} payload - The payload object to be validated.
   * @returns {Object} - An array containing the validation errors, if any.
   */
  const checkFields = (payload) => {
    const validations = {
      name: (value) => {
        if (!value) return "Please enter a Provider Name";
      },
      coplineVerified: (value) => {
        if (value === undefined)
          return "Please indicate whether the Provider is verified by Copline";
      },
      providerType: (value) => {
        if (!value) return "Please select a Provider Type";
      },
      dateVerified: (value) => {
        if (payload.coplineVerified && !value)
          return "Please enter the Date the Provider was verified by Copline";
      },
      country: (value) => {
        if (!value) return "Please select a Country";
      },
      smsPhone: (value) => {
        if (payload.hasSmsPhone && !value)
          return "Please enter a SMS Phone Number";
      },
      telehealth: (value) => {
        if (value === undefined)
          return "Please indicate whether the Provider offers Telehealth Services";
      },
    };

    const validationErrors = Object.entries(validations).reduce(
      (errors, [field, validationFn]) => {
        const error = validationFn(payload[field]);
        return error ? [...errors, error] : errors;
      },
      []
    );

    return validationErrors;
  };

  /**
   * Formats the payload object by modifying certain properties.
   *
   * @param {Object} payload - The payload object to be formatted.
   * @returns {Object} - The formatted payload object.
   */
  const formatPayload = (payload) => {
    const formatters = {
      providerType: (value) => value["value"],
      state: (value) => value["value"],
      country: (value) => value["value"],
    };

    const needsFiltering = (field) =>
      (payload[field] === "") ||
      payload[field] === null ||
      payload[field] === undefined;

    const formattedPayload = Object.entries(payload).reduce(
      (formatted, [field, value]) => {
        if (field in formatters)
          return { ...formatted, [field]: formatters[field](value) };
        return needsFiltering(field)
          ? { ...formatted, [field]: null }
          : { ...formatted, [field]: value };
      },
      {}
    );

    delete formattedPayload.hasSmsPhone;
    delete formattedPayload.tableState;

    return formattedPayload;
  };

  return { checkFields, formatPayload };
}
